import { Component, OnInit, ElementRef, ChangeDetectorRef, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import flatpickr from 'flatpickr';
import { LocalizationService } from 'src/app/services/localization.service';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { environment } from '../../../../environments/environment';
import { PrintService } from '../../../services/print.service';
import { FieldsService } from '../../../services/fields.service';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { AuthService } from '../../../services/auth.service';
import { DialogService } from '../../../services/dialog.service';
import { EntityRESTService } from '../../../services/entity-rest.service';
import { UtilityService } from '../../../services/utility.service';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { AuthRESTService } from '../../../services/auth-rest.service';
import { MenuRESTService } from '../../../services/menu-rest.service';
import { FormBuilder } from '@angular/forms';
import { RouteEventsService } from '../../../route-events.service';
import { PermissionsService } from '../../../services/permissions.service';
import { Renderer2 } from '@angular/core';
import { ConfigPagesService } from '../../../services/config-pages.service';
import { PackagesService } from '../../../services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../../services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { StudentDetailsDialogComponent } from '../student-details-dialog/student-details-dialog.component';
import { CurrencyService } from 'src/app/services/currency.service';
import { LoggerService } from '../../../services/logger.service';

interface StaffSuccessCategory {
  '# Tried': number | null;
  '# Sold': number | null;
  '% Sold': number | null;
  'Sales Value': number | null;
  '# Tried YTD': number | null;
  '# Sold YTD': number | null;
  '% Sold (YTD)': number | null;
  'Sales Value YTD': number | null;
  // Optional student details arrays
  tried_students?: any[];
  sold_students?: any[];
  tried_ytd_students?: any[];
  sold_ytd_students?: any[];
}

interface CategorySet {
  DE?: StaffSuccessCategory;
  PRE?: StaffSuccessCategory;
  ORI?: StaffSuccessCategory;
  EXT?: StaffSuccessCategory;
  REN?: StaffSuccessCategory;
  MISC?: StaffSuccessCategory;
  NON?: StaffSuccessCategory;
  SUN?: StaffSuccessCategory;
}

interface InstructorSuccessData {
  instructor_name: string;
  teaching_metrics?: CategorySet;
  executive_metrics?: CategorySet;
  // Legacy support for old format
  categories?: CategorySet;
  // Table rows, built once per load. Binding a fresh getCategoryData() array on
  // every change-detection pass re-rendered the rows constantly and dropped
  // the sticky column styling from the body cells.
  rows?: any[];
}

@Component({
  selector: 'app-staff-success-report',
  templateUrl: './staff-success-report.component.html',
  styleUrls: ['./staff-success-report.component.css']
})
export class StaffSuccessReportComponent extends ViewTableComponent implements OnInit, AfterViewInit, OnDestroy {
  override displayedColumns: string[] = [
    'category',
    'tried',
    'sold',
    'percentWk',
    'salesValueRange',
    'triedYtd',
    'soldYtd',
    'percentYtd',
    'salesValueYtd'
  ];

  year: string;
  week: string;
  quarter: string = '';
  @ViewChild('dateRangeInput') dateRangeInput!: ElementRef;
  dateTimeRange: Date[] = [];
  isDateRangeActive: boolean = false;
  dateRangeDisplayValue: string = '';
  private dateRangePicker?: flatpickr.Instance;
  private localizationService = new LocalizationService();
  trackingMethod: string = 'dual';  // Default to dual tracking
  includeStudentDetails: boolean = true;  // Default to include student details
  override instructorData: InstructorSuccessData[] = [];
  override WeeksForYearData: any[] = [];
  override LastFourYearsData: any[] = this.getLastFourYears();
  reportErrors: any[] = [];
  quarters: any[] = [
    { key: 'Q1', value: 'Q1 (Jan - Mar)' },
    { key: 'Q2', value: 'Q2 (Apr - Jun)' },
    { key: 'Q3', value: 'Q3 (Jul - Sep)' },
    { key: 'Q4', value: 'Q4 (Oct - Dec)' }
  ];


  constructor(
    override _refreshService: RefreshService,
    override _activatedRoute: ActivatedRoute,
    override _fieldsService: FieldsService,
    override _drupalRESTService: DrupalRESTService,
    override _authService: AuthService,
    override _dialogService: DialogService,
    override _entityRESTService: EntityRESTService,
    override _utilityService: UtilityService,
    override _taxonomyService: TaxonomyService,
    override _router: Router,
    override _authRESTService: AuthRESTService,
    override _menuRESTService: MenuRESTService,
    override _formBuilder: FormBuilder,
    override _routeEventsService: RouteEventsService,
    override _permissionsService: PermissionsService,
    override _renderer2: Renderer2,
    override _configPages: ConfigPagesService,
    override _packagesService: PackagesService,
    override _snackBar: MatSnackBar,
    override _userRESTService: UserService,
    override _el: ElementRef,
    override _changeDetectorRef: ChangeDetectorRef,
    override _sanitizer: DomSanitizer,
    override _studioConfigService: StudioConfigService,
    override _componentRefreshService: ComponentRefreshService,
    override _translateService: TranslateService,
    private printService: PrintService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  getCurrentWeek() {
    return this._fieldsService.getCurrentWeek().toString();
  }

  private getCurrentWeekYear(): string {
    return this._fieldsService.getCorrectYearForWeek(new Date()).toString();
  }

  get periodLabel(): string {
    return this.isDateRangeActive ? 'Period' : 'Week';
  }

  override ngOnInit(): void {
    this._activatedRoute.queryParams.subscribe(params => {
      // Check for date range parameters
      if (params['start_date'] && params['end_date']) {
        const startDate = this.reportDateService.parseCalendarDateInput(params['start_date']);
        const endDate = this.reportDateService.parseCalendarDateInput(params['end_date']);

        if (!startDate || !endDate) {
          this.dateTimeRange = [];
          this.isDateRangeActive = false;
          this.year = this.getCurrentWeekYear();
          this.week = this.getCurrentWeek();
          this.quarter = '';
          this.onYearChange(this.year);
          this.loadData();
          return;
        }

        this.dateTimeRange = [
          startDate,
          endDate
        ];
        this.isDateRangeActive = true;
        this.year = this.getCurrentWeekYear();
        this.week = this.getCurrentWeek();
        this.quarter = '';
        this.onYearChange(this.year);
        this.loadData();
      }
      // Check for quarter parameter
      else if (params['quarter'] && params['year']) {
        this.year = params['year'];
        this.quarter = params['quarter'];
        this.week = this.getCurrentWeek();
        this.onYearChange(this.year);
        this.setQuarterDateRange();
      }
      // Default to week/year
      else {
        this.isDateRangeActive = false;
        this.year = params['year'] || this.getCurrentWeekYear();
        this.week = params['week'] || this.getCurrentWeek();
        this.quarter = '';
        this.onYearChange(this.year);
        this.loadData();
      }
    });
  }

  override ngAfterContentInit(): void {
    // Keep this empty or call super if needed
  }

  ngAfterViewInit(): void {
    this.initDateRangePicker();
  }

  private initDateRangePicker(): void {
    if (!this.dateRangeInput?.nativeElement) return;

    this.datePickerConfigSubscription?.unsubscribe();
    this.datePickerConfigSubscription = this._studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);

      this.dateRangePicker = flatpickr(this.dateRangeInput.nativeElement, {
        mode: 'range',
        dateFormat: formats.flatpickrDateFormat,
        disableMobile: true,
        defaultDate: this.dateTimeRange?.length === 2 ? this.dateTimeRange : undefined,
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.dateTimeRange = selectedDates;
            this.isDateRangeActive = true;
            this.dateRangeDisplayValue = this.dateRangePicker?.input?.value || '';
            this.quarter = '';
            this.loadData();
          }
        }
      });

      if (this.dateTimeRange?.length === 2 && this.dateRangePicker) {
        this.dateRangePicker.setDate(this.dateTimeRange, false);
        this.dateRangeDisplayValue = this.dateRangePicker.input.value;
      }
    });
  }

  openDateRangePicker(): void {
    this.dateRangePicker?.open();
  }

  // Studio config is app-lifetime; released on destroy so the report can be collected.
  private datePickerConfigSubscription: Subscription;

  override ngOnDestroy(): void {
    this.dateRangePicker?.destroy();
    this.datePickerConfigSubscription?.unsubscribe();
    // Call parent destructor if needed
    super.ngOnDestroy();
  }

  onYearChange(yearOrEvent: any): void {
    // Handle both string year and event object
    if (typeof yearOrEvent === 'string') {
      this.year = yearOrEvent;
    } else if (yearOrEvent && yearOrEvent.value) {
      this.year = yearOrEvent.value;
    }
    this.WeeksForYearData = this._fieldsService.WeeksForYearData(Number(this.year));
  }

  onQuarterChange(event: any): void {
    if (event && event.value) {
      this.quarter = event.value;
      this.setQuarterDateRange();
    }
  }

  setQuarterDateRange(): void {
    if (!this.quarter || !this.year) return;

    const yearNum = parseInt(this.year);
    let startMonth: number, endMonth: number;

    switch (this.quarter) {
      case 'Q1':
        startMonth = 0; // January
        endMonth = 2;   // March
        break;
      case 'Q2':
        startMonth = 3; // April
        endMonth = 5;   // June
        break;
      case 'Q3':
        startMonth = 6; // July
        endMonth = 8;   // September
        break;
      case 'Q4':
        startMonth = 9; // October
        endMonth = 11;  // December
        break;
      default:
        return;
    }

    const startDate = new Date(yearNum, startMonth, 1);
    const endDate = new Date(yearNum, endMonth + 1, 0); // Last day of end month

    this.dateTimeRange = [startDate, endDate];
    this.isDateRangeActive = true;
    this.loadData();
  }

  loadData(): void {
    if (this.isDateRangeActive && this.dateTimeRange?.length > 0) {
      this.getData([
        { parameter: 'start_date', value: this.reportDateService.formatCalendarParam(this.dateTimeRange[0]) },
        { parameter: 'end_date', value: this.reportDateService.formatCalendarParam(this.dateTimeRange[1]) },
        { parameter: 'tracking_method', value: this.trackingMethod },
        { parameter: 'include_student_details', value: 'true' }
      ]);
    } else if (this.week && this.year) {
      this.getData([
        { parameter: 'week', value: this.week.toString() },
        { parameter: 'year', value: this.year },
        { parameter: 'tracking_method', value: this.trackingMethod },
        { parameter: 'include_student_details', value: 'true' }
      ]);
    }
  }

  override getData(params: { parameter: string; value: string; }[]): void {
    this.ShowProgressBar = true;
    this.queryParams = params;

    if (this.sortParams !== undefined) {
      this.queryParams.push(this.sortParams);
    }

    this._drupalRESTService.httpGET('/api_rest/v1/staff_success_report', params)
      .subscribe({
        next: (response) => {
          this.ShowProgressBar = false;
          this.reportErrors = [];

          this.instructorData = Object.entries(response).map(([instructor_name, data]: [string, any]) => {
            const instructor: InstructorSuccessData = {
              instructor_name,
              teaching_metrics: data.teaching_metrics,
              executive_metrics: data.executive_metrics,
              // Support legacy format if needed
              categories: data.categories || data
            };
            instructor.rows = this.getCategoryData(instructor);
            return instructor;
          });

          // Extract errors from the response data
          this.extractErrors(response);

          // Trigger change detection after data loads
          this._changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.ShowProgressBar = false;
          LoggerService.error('Error fetching staff success data:', error);
        }
      });
  }

  refreshData(): void {
    this.getData(this.queryParams);
  }

  extractErrors(response: any): void {
    // Look through the API response for any errors in the tracking data
    // Errors will be in counter['errors'] arrays from backend tracking functions

    // Note: Since we're receiving processed instructor data, we need to check if
    // there's an 'errors' key at the top level or embedded in the data structure
    if (response.errors && Array.isArray(response.errors)) {
      this.reportErrors = response.errors;
    }

    // Also check each instructor's data for errors
    Object.entries(response).forEach(([instructorName, data]: [string, any]) => {
      if (data.errors && Array.isArray(data.errors)) {
        // Add instructor name context to errors
        data.errors.forEach((error: any) => {
          this.reportErrors.push({
            ...error,
            instructor: instructorName
          });
        });
      }
    });
  }

  dismissError(index: number): void {
    this.reportErrors.splice(index, 1);
  }

  exportReport(): void {
    // Get the current query parameters
    const params: any = {};

    if (this.isDateRangeActive && this.dateTimeRange.length === 2) {
      params.start_date = this.reportDateService.formatCalendarParam(this.dateTimeRange[0]);
      params.end_date = this.reportDateService.formatCalendarParam(this.dateTimeRange[1]);
    } else {
      params.week = this.week;
      params.year = this.year;
    }

    params.tracking_method = this.trackingMethod;
    if (this.includeStudentDetails) {
      params.include_student_details = 'true';
    }

    // Create the URL with query parameters
    const baseUrl = environment.hostUrl;
    const queryString = new URLSearchParams(params).toString();
    const studioId = this._authService.getCurrentStudioId();
    const url = `${baseUrl}/api_rest/v1/staff_success_report_csv?_format=json&${queryString}&field_studio_reference_target_id=${studioId}`;

    // Trigger file download
    window.open(url, '_blank');
  }


  override resetForm(): void {
    this.dateTimeRange = [];
    this.isDateRangeActive = false;
    this.dateRangeDisplayValue = '';
    this.dateRangePicker?.clear();
    this.week = this.getCurrentWeek();
    this.year = this.getCurrentWeekYear();
    this.quarter = '';  // Reset quarter selection
    this.trackingMethod = 'dual';  // Reset to default dual tracking
    this.includeStudentDetails = true;  // Reset to default include student details
    this.onYearChange(this.year);
    this.loadData();
  }

  private getLastFourYears(): any[] {
    const currentYear = moment().year();
    return Array.from({ length: 4 }, (_, i) => ({
      key: (currentYear - i).toString(),
      value: (currentYear - i).toString()
    }));
  }

  getCategoryData(instructor: InstructorSuccessData) {
    if (!instructor) {
      return [];
    }

    const rows = [];

    // Handle dual tracking mode
    if (instructor.teaching_metrics || instructor.executive_metrics) {
      // Add teaching metrics
      if (instructor.teaching_metrics) {
        Object.entries(instructor.teaching_metrics).forEach(([category, data]: [string, any]) => {
          rows.push({
            category: `Teaching - ${category}`,
            metricsType: 'teaching',
            categoryKey: category,
            '# Tried': data['# Tried'],
            '# Sold': data['# Sold'],
            '% Sold': data['% Sold'],
            'Sales Value': data['Sales Value'],
            '# Tried YTD': data['# Tried YTD'],
            '# Sold YTD': data['# Sold YTD'],
            '% Sold (YTD)': data['% Sold (YTD)'],
            'Sales Value YTD': data['Sales Value YTD'],
            // Include student details if present
            tried_students: data.tried_students || null,
            sold_students: data.sold_students || null,
            tried_ytd_students: data.tried_ytd_students || null,
            sold_ytd_students: data.sold_ytd_students || null
          });
        });
      }

      // Add executive metrics
      if (instructor.executive_metrics) {
        Object.entries(instructor.executive_metrics).forEach(([category, data]: [string, any]) => {
          rows.push({
            category: `Executive - ${category}`,
            metricsType: 'executive',
            categoryKey: category,
            '# Tried': data['# Tried'],
            '# Sold': data['# Sold'],
            '% Sold': data['% Sold'],
            'Sales Value': data['Sales Value'],
            '# Tried YTD': data['# Tried YTD'],
            '# Sold YTD': data['# Sold YTD'],
            '% Sold (YTD)': data['% Sold (YTD)'],
            'Sales Value YTD': data['Sales Value YTD'],
            // Include student details if present
            tried_students: data.tried_students || null,
            sold_students: data.sold_students || null,
            tried_ytd_students: data.tried_ytd_students || null,
            sold_ytd_students: data.sold_ytd_students || null
          });
        });
      }
    }
    // Handle legacy format
    else if (instructor.categories) {
      Object.entries(instructor.categories).forEach(([category, data]: [string, any]) => {
        rows.push({
          category,
          metricsType: 'legacy',
          categoryKey: category,
          '# Tried': data['# Tried'],
          '# Sold': data['# Sold'],
          '% Sold': data['% Sold'],
          'Sales Value': data['Sales Value'],
          '# Tried YTD': data['# Tried YTD'],
          '# Sold YTD': data['# Sold YTD'],
          '% Sold (YTD)': data['% Sold (YTD)'],
          'Sales Value YTD': data['Sales Value YTD'],
          // Include student details if present
          tried_students: data.tried_students || null,
          sold_students: data.sold_students || null,
          tried_ytd_students: data.tried_ytd_students || null,
          sold_ytd_students: data.sold_ytd_students || null
        });
      });
    }

    return rows;
  }

  /** Longest tooltip student list before we cut over to "click to see all". */
  private readonly TOOLTIP_MAX_ROWS = 10;

  private readonly categoryLabels: Record<string, string> = {
    DE: 'DE',
    PRE: 'Pre-Original',
    ORI: 'Original',
    EXT: 'Extension',
    REN: 'Renewal',
    MISC: 'Miscellaneous',
    NON: 'Non-Unit',
    SUN: 'Sundry',
  };

  /** "week 17, 2026" or "Apr 26, 2026 - May 2, 2026", for the help text. */
  private get periodText(): string {
    if (this.isDateRangeActive && this.dateTimeRange?.length === 2) {
      return `${this.reportDateService.formatDate(this.dateTimeRange[0])} - ${this.reportDateService.formatDate(this.dateTimeRange[1])}`;
    }
    return `week ${this.week}, ${this.year}`;
  }

  private get ytdYear(): string | number {
    return this.isDateRangeActive && this.dateTimeRange?.length === 2
      ? this.dateTimeRange[1].getFullYear()
      : this.year;
  }

  /** Column header help: a plain sentence first, then the exact rule for staff who want it. */
  get columnHelp(): Record<string, string> {
    const period = this.periodText;
    const ytdRange = `week 1 of ${this.ytdYear} through the end of ${period}`;
    const more = (text: string) => `\n\nMore detail: ${text}`;
    return {
      category:
        'Teaching = credit as the student\'s teacher.\n' +
        'Executive = credit as the person who made the sale.\n' +
        'An arrow (ORI → EXT) means the student finished one program and bought the next.\n' +
        'Hover a row name for details.' +
        more('Teaching credit for DE and Pre-Original goes to whoever taught the posted lesson, not the teacher on the student\'s profile. ' +
          'Teaching credit for Original, Extension and Renewal goes to the teachers listed on the enrollment (the teacher percentages, or the enrollment\'s teacher field), and only shows on arrow rows. ' +
          'Executive rows credit the user in the enrollment\'s executive field. ' +
          'Teaching arrow rows are based on the lesson that finished the first program; Executive arrow rows are based on the origin enrollment recorded on the new sale.'),
      tried:
        `How many chances to sell in ${period}.\n` +
        'Click the number to see the students.' +
        more('Arrow rows count students who finished a program in the period. ' +
          'A program is finished when the last charged lesson uses up the lessons paid for, and that lesson\'s date is what puts it in the period. ' +
          'DE and Pre-Original credit goes to the teacher who taught that last lesson; Original, Extension and Renewal credit goes to the teachers listed on the enrollment. ' +
          'The Teaching - DE and Teaching - PRE rows count the same taught lessons. ' +
          'Executive rows without an arrow count enrollments of that type whose sale date is in the period and that list this staff member as the executive.'),
      sold:
        `How many of those chances turned into a sale in ${period}.\n` +
        'Click the number to see the enrollments.' +
        more('Arrow rows count the students from # Tried who bought the next program, with a sale date between the finished program\'s sale date and the end of the period. ' +
          'Executive Original, Extension and Renewal rows count the enrollment itself as sold. ' +
          'DE and Pre-Original rows are never sold on their own row; the sale appears on an arrow row once the student buys their next program.'),
      percent: '# Sold ÷ # Tried.' +
        more('Rounded to two decimals. Blank when # Tried is zero.'),
      value:
        `Total price of the enrollments sold in ${period}, after discounts and before tax.\n` +
        'Hover the amount to see each enrollment.' +
        more('Uses the enrollment price after discount and before tax. ' +
          'Executive rows and arrow rows use the full enrollment price. DE and Pre-Original rows never carry a value.'),
      triedYtd: `Same as the column to the left, but for all of ${this.ytdYear} so far.` +
        more(`Same rule as # Tried, counted from ${ytdRange}.`),
      soldYtd: `Same as the column to the left, but for all of ${this.ytdYear} so far.` +
        more(`Same rule as # Sold, counted from ${ytdRange}.`),
      percentYtd: '# Sold YTD ÷ # Tried YTD.' +
        more('Rounded to two decimals. Blank when # Tried YTD is zero.'),
      valueYtd: `Same as the column to the left, but for all of ${this.ytdYear} so far.` +
        more(`Same rule as $ Value, summed from ${ytdRange}.`),
    };
  }

  hasStudents(students: any[] | null | undefined): boolean {
    return Array.isArray(students) && students.length > 0;
  }

  private categoryLabel(key: string): string {
    return this.categoryLabels[key] || key;
  }

  /** "an Original", "a Renewal". */
  private a(label: string): string {
    return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}`;
  }

  /** Per-row explanation of what the row counts, in plain language. */
  getCategoryTooltip(row: any, instructor: InstructorSuccessData): string {
    const who = instructor?.instructor_name || 'this staff member';
    const key: string = row?.categoryKey || '';
    const path = key.split('→').map(part => part.trim());
    const isPath = path.length === 2;
    const label = this.categoryLabel(key);
    const firstProgram = ['DE', 'PRE'].includes(key);

    if (row?.metricsType === 'teaching') {
      if (isPath) {
        const [from, to] = path.map(k => this.categoryLabel(k));
        const fromIsFirst = ['DE', 'PRE'].includes(path[0]);
        const opening = fromIsFirst
          ? `Students ${who} taught who finished their ${from} and then bought ${this.a(to)}.\n` +
            `# Tried = students whose ${from} lesson ${who} taught (the posted lesson, not who it was booked with).\n`
          : `Students whose ${from} program lists ${who} as a teacher, who finished it and then bought ${this.a(to)}.\n` +
            `# Tried = students who finished ${this.a(from)} with ${who} on the enrollment.\n`;
        return opening +
          `# Sold = those who went on to buy ${this.a(to)}.\n` +
          `$ Value = the price of the ${to} programs they bought.`;
      }
      return `${label} lessons ${who} taught.\n` +
        `# Tried = ${label} programs where ${who} taught the posted lesson, whoever it was booked with.\n` +
        `${this.a(label).replace(/^a/, 'A')} is not counted as a sale here; it shows as a sale on the arrow row when the student buys their next program.`;
    }

    if (row?.metricsType === 'executive') {
      if (isPath) {
        const [from, to] = path.map(k => this.categoryLabel(k));
        return `${to} programs ${who} sold to students who had just finished ${this.a(from)}.\n` +
          `# Sold = those ${to} sales. # Tried is left blank because it is already counted on the Executive - ${path[1]} row.\n` +
          `$ Value = the price of those ${to} programs.`;
      }
      return `${label} programs where ${who} is listed as the executive who made the sale.\n` +
        (firstProgram
          ? `# Tried = ${label} programs sold. ${this.a(label).replace(/^a/, 'A')} is not counted as a sale here; it shows as a sale on the arrow row when the student buys their next program.\n`
          : `# Tried and # Sold = ${label} programs sold.\n`) +
        `$ Value = the full price of those programs.`;
    }

    return `${label} programs credited to ${who}.`;
  }

  private describeStudent(student: any): string {
    const parts: string[] = [student?.student_name || 'Unknown'];
    if (student?.enrollment_id) {
      parts.push(`Enr #${student.enrollment_id}`);
    }
    if (student?.sale_date) {
      parts.push(`sold ${this.reportDateService.formatDate(student.sale_date)}`);
    }
    if (student?.completed_date && !student?.follow_on_category) {
      parts.push(`finished ${this.reportDateService.formatDate(student.completed_date)}`);
    }
    if (student?.origin_category) {
      parts.push(`from ${student.origin_category}`);
    }
    if (student?.follow_on_category) {
      parts.push(`→ ${student.follow_on_category}`);
    }
    return parts.join(' · ');
  }

  private moreLine(total: number): string {
    return total > this.TOOLTIP_MAX_ROWS
      ? `\n+${total - this.TOOLTIP_MAX_ROWS} more — click to see all`
      : '\nClick to see details';
  }

  /** Hover list of the students behind a # Tried / # Sold number. */
  getStudentsTooltip(students: any[] | null | undefined, label: string): string {
    if (!this.hasStudents(students)) {
      return '';
    }
    const lines = students.slice(0, this.TOOLTIP_MAX_ROWS).map(s => this.describeStudent(s));
    return `${label} (${students.length})\n${lines.join('\n')}${this.moreLine(students.length)}`;
  }

  /** Hover math behind a % Sold cell. */
  getPercentTooltip(row: any, ytd: boolean): string {
    const tried = row?.[ytd ? '# Tried YTD' : '# Tried'];
    const sold = row?.[ytd ? '# Sold YTD' : '# Sold'];
    const pct = row?.[ytd ? '% Sold (YTD)' : '% Sold'];
    if (pct === null || pct === undefined) {
      return tried ? `${sold || 0} sold ÷ ${tried} tried` : 'No tried enrollments, so no percentage';
    }
    return `${sold || 0} sold ÷ ${tried || 0} tried = ${pct}%`;
  }

  /** Hover breakdown of the enrollments summed into a $ Value cell. */
  getSalesValueTooltip(row: any, ytd: boolean): string {
    const total = row?.[ytd ? 'Sales Value YTD' : 'Sales Value'];
    const students: any[] = row?.[ytd ? 'sold_ytd_students' : 'sold_students'] || [];
    if (total === null || total === undefined) {
      return '';
    }
    const amountOf = (s: any): number | null => {
      const v = s?.credited_value ?? s?.sales_value;
      return v === null || v === undefined || v === '' ? null : Number(v);
    };
    const hasAmounts = students.some(s => amountOf(s) !== null);
    const lines = students.slice(0, this.TOOLTIP_MAX_ROWS).map(s => {
      const amount = amountOf(s);
      const who = s?.student_name || 'Unknown';
      const enr = s?.enrollment_id ? ` (Enr #${s.enrollment_id})` : '';
      return amount === null ? `${who}${enr}` : `${who}${enr}: ${this.currencyService.format(amount)}`;
    });
    const header = hasAmounts
      ? `${this.currencyService.format(total)} from ${students.length} sold enrollment${students.length === 1 ? '' : 's'}`
      : `${this.currencyService.format(total)} — price after discount, before tax` +
        (students.length ? ` of ${students.length} sold enrollment${students.length === 1 ? '' : 's'}` : '');
    if (!students.length) {
      return header;
    }
    return `${header}\n${lines.join('\n')}${this.moreLine(students.length)}`;
  }

  showStudentDetails(students: any[], columnName: string): void {
    if (!students || students.length === 0) {
      return;
    }

    this._dialogService.openDialog(
      StudentDetailsDialogComponent,
      'defaultWithData',
      {
        students: students,
        columnName: columnName
      }
    );
  }

  printReport() {
    const dateInfo = this.isDateRangeActive && this.dateTimeRange.length === 2
      ? `Date Range: ${this.reportDateService.formatDate(this.dateTimeRange[0])} - ${this.reportDateService.formatDate(this.dateTimeRange[1])}`
      : `Week ${this.week}, ${this.year}`;

    const periodLabel = this.isDateRangeActive ? 'Period' : 'Weekly';

    // Build the HTML content for printing
    let content = `
      <div class="report-container">
        <h1 class="report-title">Staff Success Report</h1>
        <h2 class="report-subtitle">${dateInfo}</h2>`;

    // Add instructor tables
    if (this.instructorData && this.instructorData.length > 0) {
      this.instructorData.forEach(instructor => {
        content += `
          <div class="instructor-section">
            <h3 class="instructor-name">${instructor.instructor_name}</h3>
            <table class="report-table">
              <thead>
                <tr>
                  <th rowspan="2">Category</th>
                  <th colspan="3">${periodLabel}</th>
                  <th colspan="3">Year to Date</th>
                </tr>
                <tr>
                  <th># Tried</th>
                  <th># Sold</th>
                  <th>% Sold</th>
                  <th># Tried</th>
                  <th># Sold</th>
                  <th>% Sold</th>
                </tr>
              </thead>
              <tbody>`;

        this.getCategoryData(instructor).forEach(row => {
          content += `
                <tr>
                  <td>${row.category}</td>
                  <td>${row['# Tried'] ?? '-'}</td>
                  <td>${row['# Sold'] ?? '-'}</td>
                  <td>${(row['% Sold'] ?? null) !== null ? row['% Sold'] + '%' : '-'}</td>
                  <td>${row['# Tried YTD'] ?? '-'}</td>
                  <td>${row['# Sold YTD'] ?? '-'}</td>
                  <td>${(row['% Sold (YTD)'] ?? null) !== null ? row['% Sold (YTD)'] + '%' : '-'}</td>
                </tr>`;
        });

        content += `
              </tbody>
            </table>
          </div>`;
      });
    } else {
      content += `
        <div class="no-data-message">
          No data available for this period
        </div>`;
    }

    content += `</div>`;

    // Define custom styles for the report
    const styles = `
      .report-container {
        font-family: Arial, sans-serif;
        margin: 0 auto;
        max-width: 1200px;
        padding: 20px;
      }
      .report-title {
        font-size: 24px;
        font-weight: bold;
        text-align: center;
        margin-bottom: 10px;
      }
      .report-subtitle {
        font-size: 18px;
        text-align: center;
        color: #666;
        margin-bottom: 20px;
      }
      .instructor-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .instructor-name {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 10px;
        color: #333;
      }
      .report-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      .report-table th,
      .report-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: center;
      }
      .report-table th {
        background-color: #f2f2f2;
        font-weight: bold;
      }
      .report-table tr:nth-child(even) {
        background-color: #f9f9f9;
      }
      .no-data-message {
        text-align: center;
        font-style: italic;
        color: #999;
        margin: 30px 0;
      }
      @media print {
        .instructor-section {
          page-break-inside: avoid;
        }
      }
    `;

    // Use the PrintService to print the report with proper title
    const printTitle = this.isDateRangeActive && this.dateTimeRange.length === 2
      ? `Staff Success Report - ${this.reportDateService.formatDate(this.dateTimeRange[0])} to ${this.reportDateService.formatDate(this.dateTimeRange[1])}`
      : `Staff Success Report - Week ${this.week}, ${this.year}`;

    this.printService.print(content, {
      title: printTitle,
      styles: styles,
      showSchoolName: true,
      promptPrint: true
    });
  }
}
